/**
 * What a screen needs from what it is handed, checked when it is built.
 *
 * `src/ui` had no tests until step 69 and STATE.md §7 said why: every *rule* a
 * screen renders is tested where the rule lives, and a screen reads rather than
 * decides, so there is little in one worth asserting. What that left untested
 * was **wiring** — and wiring in this codebase fails in a particular, quiet way.
 *
 * Every screen is defensive. `campaign.gate.rank`, `who.heat?.fraction ?? 0`,
 * `setText(node, said ?? '')`. That is right for a `bank` that is genuinely
 * optional and wrong for everything else, because the two cases look identical
 * from the outside: rename `campaign.offer` to `campaign.cards` and the hub does
 * not throw, it renders a screen with **no fights on it**. Nothing fails. The
 * suite stays green. Somebody notices a week later.
 *
 * So this is the same idea the project already applies to data — `validateMoves`,
 * `validateLadder`, `validateAudio`, all of them failing at boot with a sentence
 * rather than at the moment somebody needs the value — applied to the objects a
 * screen is handed. A screen declares what it reads; a missing key is a crash
 * naming the screen, the source and the key, on the frame the screen is built.
 *
 * Two things it deliberately does *not* do.
 *
 * It does not check **types or values**, only presence. A getter that exists and
 * returns `undefined` is a stale-value bug rather than a wiring one, and it is
 * the tests' job — one of the three failure modes this whole step is about.
 *
 * It does not run per frame. Construction only: a contract is about the shape of
 * an object, and an object does not change shape halfway through a round.
 */

export class UiContractError extends Error {}

/**
 * Throws unless `source` has every one of `keys`.
 *
 * `who` is the screen and `what` is the handle, because the error is read by
 * somebody who has just renamed something and needs to know which of seven
 * screens was still asking for the old name.
 *
 * A getter that throws when read is treated as present: the contract is about
 * whether the screen is asking for something that exists, and a getter that
 * exists and is angry is a different bug with a different stack trace.
 */
export function needs(source, keys, { who, what }) {
  if (source === null || source === undefined) {
    throw new UiContractError(`${who} needs a ${what} and was given ${JSON.stringify(source)}`);
  }
  const missing = keys.filter((key) => !has(source, key));
  if (missing.length) {
    throw new UiContractError(
      `${who} reads ${what}.${missing.join(`, ${what}.`)}, which ${
        missing.length === 1 ? 'is' : 'are'
      } not there`
    );
  }
  return source;
}

/** The same, for a handle a screen can genuinely do without. */
export function needsIf(source, keys, options) {
  return source === null || source === undefined ? source : needs(source, keys, options);
}

/**
 * Whether `key` is readable off `source`, getters and prototypes included.
 *
 * `in` rather than `Object.hasOwn`, because almost everything in this project
 * is a closure returning an object literal with getters on it, and a few are
 * `Object.freeze`d results of a spread — both answer `in` and only one answers
 * `hasOwn`. A key whose value is `undefined` still counts as present; see the
 * header for why that is the tests' problem rather than this one's.
 */
function has(source, key) {
  try {
    return key in Object(source);
  } catch {
    return false;
  }
}
